import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Footer from '../../layout/Footer';
import Navbar from '../../layout/Navbar';

const TutorPayment = ({ 
  tutorData = {},
  onPaymentSuccess,
  onPaymentError,
  onCancel 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    tutorId: '',
    firstname: '',
    lastname: '',
    email: '',
    phonenumber: '',
    amount: '26.00'
  });
  
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [agreement, setAgreement] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [applicationData, setApplicationData] = useState(null);
  
  // Configuration
  const config = {
    apiUrl: 'https://pastq.eddcodesgh.com/qApi/client/tutors/initiate-expresspay.php',
    apiKey: 'f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506',
  };
  
  // Initialize form data and get application data
  useEffect(() => {
    if (!isInitialized) {
      // Try to get application data from multiple sources
      const getApplicationData = () => {
        // 1. Try from location state (passed from application form)
        if (location.state?.applicationId) {
          return {
            applicationId: location.state.applicationId,
            fullName: location.state.fullName || '',
            email: location.state.email || '',
            phone: location.state.phone || ''
          };
        }
        
        // 2. Try from localStorage
        const appId = localStorage.getItem('tutorAppId');
        if (appId) {
          return {
            applicationId: appId,
            fullName: localStorage.getItem('fullName') || '',
            email: localStorage.getItem('email') || '',
            phone: localStorage.getItem('phone') || ''
          };
        }
        
        // 3. Try from sessionStorage
        const sessionAppId = sessionStorage.getItem('tutorAppId');
        if (sessionAppId) {
          return {
            applicationId: sessionAppId,
            fullName: sessionStorage.getItem('fullName') || '',
            email: sessionStorage.getItem('email') || '',
            phone: sessionStorage.getItem('phone') || ''
          };
        }
        
        return null;
      };
      
      const appData = getApplicationData();
      
      if (!appData) {
        console.error('No application data found!');
        toast.error('No application data found. Please submit your application first.');
        setTimeout(() => navigate('/apply-as-tutor'), 2000);
        return;
      }
      
      setApplicationData(appData);
      
      const tutorId = appData.applicationId;
      
      // Parse full name
      const nameParts = (appData.fullName || '').split(' ');
      const firstname = nameParts[0] || '';
      const lastname = nameParts.slice(1).join(' ') || '';
      
      const initialData = {
        tutorId: tutorId,
        firstname: firstname,
        lastname: lastname,
        email: appData.email || '',
        phonenumber: appData.phone || '',
        amount: '26.00'
      };
  
      setFormData(initialData);
      setIsInitialized(true);
      
      // Also set these values in localStorage for backup
      if (!localStorage.getItem('tutorAppId') && appData.applicationId) {
        localStorage.setItem('tutorAppId', appData.applicationId);
        if (appData.fullName) localStorage.setItem('fullName', appData.fullName);
        if (appData.email) localStorage.setItem('email', appData.email);
        if (appData.phone) localStorage.setItem('phone', appData.phone);
      }
    }
  }, [tutorData, isInitialized, location.state, navigate]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const validate = () => {
    if (!formData.tutorId) {
      toast.error('Application ID not found. Please submit your application first.');
      return 'Application ID missing';
    }
    
    if (!formData.firstname.trim()) {
      toast.error('First name is required');
      return 'First name is required';
    }
    
    if (!formData.lastname.trim()) {
      toast.error('Last name is required');
      return 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      toast.error('Email is required');
      return 'Email is required';
    }
    
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return 'Valid email required';
    }
    
    if (!formData.phonenumber.trim()) {
      toast.error('Phone number is required');
      return 'Phone number is required';
    }
    
    if (!/^(\+233|0)[0-9]{9}$/.test(formData.phonenumber.replace(/\s/g, ''))) {
      toast.error('Please enter a valid Ghanaian phone number (format: +233XXXXXXXXX or 0XXXXXXXXX)');
      return 'Valid Ghanaian phone required';
    }
    
    if (!agreement) {
      toast.error('You must accept the terms and conditions');
      return 'Accept terms to continue';
    }
    
    return null;
  };
  
  const handlePayment = async (e) => {
    e.preventDefault();
    
    const error = validate();
    if (error) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Generate order ID
      const orderId = `TUTOR_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
     
      // Call your backend API
      const response = await axios.post(config.apiUrl, {
        tutorId: formData.tutorId,
        firstname: formData.firstname.trim(),
        lastname: formData.lastname.trim(),
        email: formData.email.trim(),
        phonenumber: formData.phonenumber.trim().replace(/\s/g, ''),
        amount: formData.amount,
        orderId: orderId,
        orderDesc: `Tutor Registration - ${formData.firstname} ${formData.lastname}`,
        applicationData: applicationData
      }, {
        headers: {
          'Authorization': `Bearer ${config.apiKey}`,
          'Content-Type': 'application/json'
        },
        timeout: 30000
      });
      
      console.log('Payment API Response:', response.data);
      
      if (response.data.status === 'success') {
        // Save payment data
        const paymentData = {
          ...formData,
          orderId: response.data.data.orderId || orderId,
          applicationId: formData.tutorId,
          timestamp: new Date().toISOString()
        };
        
        localStorage.setItem('tutorPayment', JSON.stringify(paymentData));
        
        // Save order ID for callback
        localStorage.setItem('currentOrderId', orderId);
        
        toast.success('Payment initiated successfully! Redirecting to payment gateway...');
        
        // Redirect to payment URL
        setTimeout(() => {
          const paymentUrl = response.data.data.checkoutUrl || 
                            response.data.data.paymentUrl || 
                            response.data.data.redirectUrl;
          
          if (paymentUrl) {
            window.location.href = paymentUrl;
          } else {
            toast.error('No payment URL received from server');
            setLoading(false);
          }
        }, 1500);
        
      } else {
        const errorMsg = response.data.message || 'Payment initiation failed';
        console.error('Payment API error:', errorMsg);
        toast.error(errorMsg);
        
        if (onPaymentError) {
          onPaymentError(errorMsg);
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Network error. Please try again.';
      
      toast.error(errorMessage);
      
      if (onPaymentError) {
        onPaymentError(errorMessage);
      }
    } finally {
      if (!status) {
        setLoading(false);
      }
    }
  };
  
  // Show loading state while initializing
  if (!isInitialized && !applicationData) {
    return (
      <>
        <Navbar />
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-md-6">
              <div className="card shadow border-0">
                <div className="card-body text-center p-5">
                  <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <h3>Loading Payment Information...</h3>
                  <p className="text-muted">Please wait while we retrieve your application data.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  
  if (status) {
    return (
      <>
        <Navbar />
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-md-6">
              <div className={`card border-${status.success ? 'success' : status.pending ? 'warning' : 'danger'} shadow`}>
                <div className="card-body text-center p-5">
                  <div className={`display-4 mb-3 text-${status.success ? 'success' : status.pending ? 'warning' : 'danger'}`}>
                    {status.success ? '✅' : status.pending ? '⏳' : '❌'}
                  </div>
                  <h3 className="card-title mb-3">
                    {status.success ? 'Payment Successful!' : 
                     status.pending ? 'Payment Pending' : 'Payment Failed'}
                  </h3>
                  <p className="card-text mb-4">
                    {status.message}
                  </p>
                  
                  {status.success && status.data && (
                    <div className="bg-light p-4 rounded mb-4 text-start">
                      <h6 className="fw-bold mb-3">Transaction Details:</h6>
                      <p><strong>Application ID:</strong> {formData.tutorId}</p>
                      <p><strong>Order ID:</strong> {status.data.orderId || status.data['order-id']}</p>
                      {status.data.transactionId && (
                        <p><strong>Transaction ID:</strong> {status.data.transactionId}</p>
                      )}
                      <p><strong>Amount:</strong> GHS {status.data.amount || '26.00'}</p>
                      {status.data.date && (
                        <p><strong>Date:</strong> {status.data.date}</p>
                      )}
                    </div>
                  )}
                  
                  <div className="d-grid gap-2">
                    {status.success ? (
                      <>
                        <button 
                          onClick={() => {
                            localStorage.removeItem('tutorAppId');
                            localStorage.removeItem('fullName');
                            localStorage.removeItem('email');
                            localStorage.removeItem('phone');
                            sessionStorage.removeItem('tutorAppId');
                            navigate('/tutor/dashboard');
                          }} 
                          className="btn btn-success btn-lg"
                        >
                          <i className="bi bi-speedometer2 me-2"></i>
                          Go to Dashboard
                        </button>
                        <button 
                          onClick={() => navigate('/tutor/profile')} 
                          className="btn btn-outline-success"
                        >
                          Complete Profile
                        </button>
                      </>
                    ) : status.pending ? (
                      <button 
                        onClick={() => navigate('/')} 
                        className="btn btn-warning"
                      >
                        Return Home
                      </button>
                    ) : (
                      <button 
                        onClick={() => window.location.reload()} 
                        className="btn btn-primary"
                      >
                        Try Again
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  
  return (
    <>
      <Navbar />
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            {/* Header */}
            <div className="text-center mb-4">
              <h2 className="h2 fw-bold mb-3">Tutor Registration Payment</h2>
              <div className="alert alert-primary border-primary border-2 rounded-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="fw-bold mb-1">One-time Registration Fee</h5>
                    <small className="text-muted">Required to activate your account</small>
                    {applicationData && (
                      <div className="mt-2 small">
                        <strong>Application ID:</strong> {applicationData.applicationId}
                      </div>
                    )}
                  </div>
                  <div>
                    <h2 className="fw-bold text-primary mb-0">GHS 26.00</h2>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Payment Form */}
            <div className="card shadow-lg border-0">
              <div className="card-header bg-primary text-white py-3">
                <h5 className="mb-0">
                  <i className="bi bi-credit-card me-2"></i>
                  Payment Information
                </h5>
              </div>
              
              <div className="card-body p-4">
                <form onSubmit={handlePayment} id="paymentForm">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        First Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="firstname"
                        className="form-control"
                        value={formData.firstname || ''}
                        onChange={handleChange}
                        required
                        placeholder="John"
                      />
                    </div>
                    
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Last Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="lastname"
                        className="form-control"
                        value={formData.lastname || ''}
                        onChange={handleChange}
                        required
                        placeholder="Doe"
                      />
                    </div>
                    
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Email Address <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        className="form-control"
                        value={formData.email || ''}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                      />
                    </div>
                    
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Phone Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phonenumber"
                        className="form-control"
                        value={formData.phonenumber || ''}
                        onChange={handleChange}
                        required
                        placeholder="+233501234567"
                      />
                      <small className="text-muted">Format: +233XXXXXXXXX or 0XXXXXXXXX</small>
                    </div>
                    
                    <div className="col-12">
                      <div className="alert alert-info border-info">
                        <div className="d-flex align-items-center">
                          <i className="bi bi-info-circle fs-4 text-info me-3"></i>
                          <div>
                            <h6 className="fw-bold mb-1">Payment Details</h6>
                            <p className="mb-2">
                              <span className="fs-4 fw-bold text-primary">GHS 26.00</span>
                              <small className="text-muted ms-2">(One-time registration fee)</small>
                            </p>
                            {applicationData?.applicationId && (
                              <p className="mb-0 small">
                                <strong>Application Reference:</strong> {applicationData.applicationId}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-12">
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="agreement"
                          checked={agreement}
                          onChange={(e) => setAgreement(e.target.checked)}
                          required
                        />
                        <label className="form-check-label" htmlFor="agreement">
                          I agree to the{' '}
                          <a href="/terms" className="text-decoration-none fw-semibold" target="_blank" rel="noopener noreferrer">
                            Terms & Conditions
                          </a>{' '}
                          and confirm this is a non-refundable registration fee.
                        </label>
                      </div>
                    </div>
                    
                    <div className="col-12">
                      <button
                        type="submit"
                        className="btn btn-primary btn-lg w-100 py-3 fw-bold"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Processing Payment...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-lock-fill me-2"></i>
                            Pay GHS 26.00 Now
                          </>
                        )}
                      </button>
                      
                      {onCancel && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary w-100 mt-3 py-2"
                          onClick={() => navigate('/tutor/apply')}
                          disabled={loading}
                        >
                          <i className="bi bi-arrow-left me-2"></i>
                          Back to Application
                        </button>
                      )}
                    </div>
                    <div className="col-12">
                      <Link to="/" className="btn btn-outline-secondary  btn-lg w-100 py-1 fw-bold"><span>skip</span></Link>
                    </div>
                  </div>
                </form>
                
                {/* Payment Methods */}
                <div className="mt-4 pt-4 border-top">
                  <h6 className="fw-bold mb-3">
                    <i className="bi bi-shield-check text-primary me-2"></i>
                    Secure Payment Methods
                  </h6>
                  
                  <div className="row g-3">
                    <div className="col-4">
                      <div className="text-center border rounded p-3">
                        <i className="bi bi-phone text-primary fs-3 mb-2 d-block"></i>
                        <span className="small fw-semibold">Mobile Money</span>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="text-center border rounded p-3">
                        <i className="bi bi-credit-card text-success fs-3 mb-2 d-block"></i>
                        <span className="small fw-semibold">Card Payment</span>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="text-center border rounded p-3">
                        <i className="bi bi-bank text-warning fs-3 mb-2 d-block"></i>
                        <span className="small fw-semibold">Bank Transfer</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center mt-3">
                    <small className="text-muted">
                      <i className="bi bi-lock me-1"></i>
                      Secured by ExpressPay • Your information is encrypted
                    </small>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Support */}
            <div className="text-center mt-4 p-3 bg-light rounded">
              <small className="text-muted">
                Need help with payment?{' '}
                <a href="mailto:support@pastq.eddcodesgh.com" className="text-decoration-none fw-semibold">
                  Contact our support team
                </a>
              </small>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

TutorPayment.defaultProps = {
  tutorData: {},
  onPaymentSuccess: () => {},
  onPaymentError: () => {},
  onCancel: () => window.history.back()
};

export default TutorPayment;